import { useEffect, useId, useRef } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/* Anything, over the page rather than inside it.
 *
 * **Built on the native `<dialog>` and `showModal()`**, which buys a real focus trap, Escape, a
 * `::backdrop` and the rest of the page made inert — none of it re-implemented. `Drawer` states
 * plainly why it does *not* trap focus: a trap has to enumerate every focusable descendant and a
 * panel of arbitrary content gets that wrong. `showModal` is the platform doing exactly that
 * enumeration, so the reasoning that rules out a hand-rolled trap is the reasoning for using this.
 *
 * **Extracted from `ConfirmDialog`, which now renders through it.** That component had the whole
 * lifecycle inline — `showModal`, the `onCancel` that catches Escape *and* the platform's backdrop
 * dismissal, the target comparison that separates a backdrop click from a click on the content.
 * Every one of those is a fact about `<dialog>` rather than about confirming something, and a second
 * copy for the add-user form would be a second answer to "what does Escape do" in the one place a
 * disagreement is invisible: both would look right in isolation. One mechanism, two callers.
 *
 * **`onClose` is one path for all three dismissals** — Escape, the backdrop, and whatever control
 * the caller renders — so a caller cannot handle two of the three and forget the last.
 *
 * **The close glyph is optional, and `ConfirmDialog` declines it.** A dialog asking a question with
 * exactly two labelled answers should not offer a third, unlabelled one: for a destructive confirm,
 * "did the × cancel, or only hide the question" is precisely the ambiguity the two named buttons
 * exist to remove, and it would sit one tab stop *before* Cancel — so the first thing a keyboard user
 * reaches would be the control whose meaning is unstated. Escape and the backdrop still reach
 * `onClose`, which is what that component already did, so declining the glyph removes nothing.
 *
 * **No `autoFocus` and no initial-focus prop.** `showModal` moves focus itself and an autofocused
 * element is not reliably honoured after it; `ConfirmDialog` needs Cancel focused for a destructive
 * question and does that itself, by querying the first button of the action row it owns — this
 * component is its child, so `showModal` has already run by the time that effect fires. A general
 * prop would invite a
 * caller to focus a text field, which is the wrong first stop for a screen reader arriving at a
 * dialog it has not heard the title of yet.
 */

export interface ModalProps {
  readonly open: boolean;
  /** Announced as the dialog's name, and rendered as its heading. */
  readonly title: string;
  /** Optional line under the title. A dialog that asks for several fields usually needs one. */
  readonly hint?: string;
  readonly children: ReactNode;
  readonly onClose: () => void;
  /** How wide the surface is, named after what it holds rather than in pixels — see `SURFACE`. */
  readonly size?: ModalSize;
  /** See the note on the close control above. Defaults to true; only a two-answer question sets it
   * false. */
  readonly closable?: boolean;
  /** A modifier on the surface this already renders — **a variant, never a replacement.**
   *
   * Merged *after* `SURFACE[size]` rather than in place of it, which is `Button`'s recorded lesson
   * one component over: there a caller's class landed after the computed one and took `lq-btn`, the
   * tone and the size with it, leaving an unstyled native element that still carried the class the
   * caller asked for. So the width rule, the centring and the backdrop are not a caller's to lose.
   *
   * It exists because the reference styles a dialog per screen — square corners, an accent top rule
   * — and adopting that one screen at a time needs somewhere to put the difference. A caller that
   * wants a *different* box wants a different `size`. */
  readonly className?: string | undefined;
}

type ModalSize = 'sentence' | 'narrow' | 'wide';

/* The three widths, as a total map rather than a ternary, so a fourth size is a compile error here
 * rather than a silent fall-through to the narrow one.
 *
 * `sentence` is `.lq-confirm`, which is not a second dialog family: that class is one width rule
 * sharing the base rule with `.lq-modal`, and 420px is deliberately narrower than a form's 520
 * because a line of prose set 520px wide reads as a paragraph rather than as a question. Keeping the
 * class is also what makes this refactor invisible on screen — `ConfirmDialog` is the same box it
 * was, with the lifecycle moved out from under it. */
const SURFACE = {
  sentence: 'lq-confirm',
  narrow: 'lq-modal',
  wide: 'lq-modal lq-modal--wide',
} satisfies Record<ModalSize, string>;

export function Modal({
  open,
  title,
  hint,
  children,
  onClose,
  size = 'narrow',
  closable = true,
  className,
}: ModalProps): ReactElement | null {
  const { t } = useTranslation();
  const dialog = useRef<HTMLDialogElement>(null);
  /* A generated id rather than a literal, so two of these on one page do not both announce the
   * first one's title. */
  const titleId = useId();

  useEffect(() => {
    const node = dialog.current;
    if (node === null || node.open) return;
    node.showModal();
    /* **There is no `close()` branch.** The subtree unmounts when `open` goes false — see the early
     * return — and removing the element ends its modal state, so a branch calling `close()` on a
     * node that no longer exists would be dead code that reads as the mechanism. */
  }, [open]);

  /* Rendered only while open. A `<dialog>` left in the tree is inert and harmless, but keeping the
   * subtree mounted would preserve state across two openings — a form still holding the last
   * attempt's values, or a stale refusal shown the moment the dialog reappears. */
  if (!open) return null;

  return (
    <dialog
      ref={dialog}
      className={
        className === undefined ? SURFACE[size] : `${SURFACE[size]} ${className}`
      }
      /* Fires for Escape and for the backdrop dismissal the platform performs, so both reach the
       * caller through the same path as its own close control. Without it, Escape would close the
       * element while the caller still believed it open, and the next `open` would find `node.open`
       * false and re-show it — a dialog that reappears after Escape. */
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      /* A click on the backdrop lands on the dialog element itself and never on its children, so
       * comparing the target is how the two are told apart. Not a `::backdrop` listener, which does
       * not receive events. */
      onClick={(event) => {
        if (event.target === dialog.current) onClose();
      }}
      aria-labelledby={titleId}
    >
      <header className="lq-modal__head">
        <div className="lq-modal__heading">
          <h2 className="lq-modal__title" id={titleId}>
            {title}
          </h2>
          {hint === undefined ? null : <p className="lq-modal__hint">{hint}</p>}
        </div>
        {/* A real button rather than a styled span, so it is a tab stop and Enter works on it. The
            glyph is `aria-hidden` and the accessible name is on the button, because "×" read aloud
            is not a name. */}
        {closable ? (
          <button
            type="button"
            className="lq-modal__close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <span aria-hidden="true">×</span>
          </button>
        ) : null}
      </header>
      <div className="lq-modal__body">{children}</div>
    </dialog>
  );
}
